/** Who is woken, and when (spec/21 §7). */
export const ALERT_TIERS = ['page', 'ticket'];
export const ALERT_CATEGORIES = ['availability', 'latency', 'errors', 'money', 'fleet', 'cost', 'business'];
const CATALOGUE = [
    // -------------------------------------------------------------------------------------------
    // Page — spec/21 §7
    // -------------------------------------------------------------------------------------------
    {
        id: 'api-availability',
        title: 'The API is failing',
        tier: 'page',
        category: 'availability',
        summary: 'More than 1% of /v1 requests are returning 5xx. The 99.9% monthly budget is about 43 minutes.',
        condition: {
            query: {
                kind: 'ratio',
                legend: 'error ratio',
                metric: 'http.server.request.duration',
                filters: [{ attribute: 'http.response.status_code', op: '=~', value: '5..' }],
                window: '5m',
            },
            op: '>',
            value: 0.01,
            forDuration: '5m',
        },
        dashboard: 'slo',
        panel: 'availability',
        runbook: 'api-availability',
        spec: 'spec/21 §1 (99.9% monthly), §7 (page for availability)',
    },
    {
        id: 'auth-outage',
        title: 'Sign-ins are failing',
        tier: 'page',
        category: 'availability',
        summary: 'Sign-in failures across methods. Nobody can start a session, and the kiosk is a locked screen.',
        condition: {
            query: {
                kind: 'ratio',
                legend: 'failed sign-ins',
                metric: 'clavian.auth.sign_ins',
                filters: [{ attribute: 'clavian.sign_in.outcome', op: '!=', value: 'succeeded' }],
                window: '5m',
            },
            op: '>',
            value: 0.5,
            forDuration: '5m',
        },
        dashboard: 'api',
        panel: 'errors-by-release',
        runbook: 'auth-outage',
        spec: 'spec/21 §7 (page for auth outage)',
    },
    {
        id: 'payment-settlement-failure',
        title: 'Money is not settling',
        tier: 'page',
        category: 'money',
        summary: 'Captured payments are not reaching organisation payout accounts within the expected window.',
        condition: {
            query: {
                kind: 'quantile',
                quantile: 0.95,
                legend: 'settlement lag p95',
                metric: 'clavian.payment.settlement.lag',
                window: '1h',
            },
            op: '>',
            // Two banking days. Beyond that it is not lag, it is a failure, and a venue has noticed.
            value: 172_800,
            forDuration: '30m',
        },
        dashboard: 'payments',
        panel: 'settlement-lag',
        runbook: 'settlement-failure',
        spec: 'spec/21 §7 (page for payment settlement failures)',
    },
    {
        id: 'ledger-integrity-failure',
        title: 'The ledger does not balance',
        tier: 'page',
        category: 'money',
        summary: 'Debits and credits disagree at the last integrity run. Play time lost or invented is a ' +
            'promise in spec/21 §1; this is that promise broken.',
        condition: {
            query: { kind: 'value', legend: 'imbalance', metric: 'clavian.ledger.imbalance', by: ['clavian.currency'] },
            op: '!=',
            value: 0,
            forDuration: '0m',
        },
        dashboard: 'ledger',
        panel: 'imbalance',
        runbook: 'ledger-integrity',
        spec: 'spec/21 §1 (zero play time lost or invented), §7 (page for ledger integrity failures)',
    },
    {
        id: 'ledger-integrity-not-running',
        title: 'The ledger integrity job has stopped running',
        tier: 'page',
        category: 'money',
        summary: 'No integrity run in 26 hours. A job that has stopped is as bad as a job that failed and is ' +
            'much easier to miss: the imbalance panel stays green because nothing recomputed it.',
        condition: {
            query: { kind: 'total', legend: 'runs', metric: 'clavian.ledger.integrity.runs', window: '26h' },
            op: '<',
            value: 1,
            forDuration: '0m',
        },
        dashboard: 'ledger',
        panel: 'runs',
        runbook: 'ledger-integrity',
        spec: 'spec/21 §7 (page for ledger integrity failures)',
    },
    {
        id: 'mass-station-offline',
        title: 'A large part of the fleet is offline',
        tier: 'page',
        category: 'fleet',
        summary: 'Stations have lost their sockets in numbers. Either the gateway or a region has gone.',
        condition: {
            query: { kind: 'value', legend: 'stations online', metric: 'clavian.station.online', window: '5m' },
            op: '<',
            // Under half of what was online five minutes ago. An absolute count would be wrong for both
            // a fleet of forty and a fleet of fifty thousand.
            value: 0.5,
            baseline: { kind: 'relative', offset: '5m' },
            forDuration: '5m',
        },
        dashboard: 'fleet',
        panel: 'online',
        runbook: 'mass-station-offline',
        spec: 'spec/21 §7 (page for mass station offline)',
    },
    {
        id: 'venue-offline-in-opening-hours',
        title: 'A venue is dark while it should be trading',
        tier: 'page',
        category: 'business',
        summary: 'No station is online at a venue that is inside its opening hours. The venue is losing money ' +
            'for every minute this holds.',
        condition: {
            query: { kind: 'value', legend: 'stations online', metric: 'clavian.station.online', by: ['clavian.venue'] },
            op: '<',
            value: 1,
            forDuration: '10m',
        },
        dashboard: 'fleet',
        panel: 'online',
        runbook: 'mass-station-offline',
        // The tier is this repository's decision, not the specification's. See the header.
        spec: 'spec/21 §6 (a venue offline in opening hours) — tier chosen here, see the header of this file',
    },
    // -------------------------------------------------------------------------------------------
    // Ticket — spec/21 §7
    // -------------------------------------------------------------------------------------------
    {
        id: 'api-latency-degraded',
        title: 'The API is slower than the SLO',
        tier: 'ticket',
        category: 'latency',
        summary: 'p95 read latency is over 400 ms. Requests are succeeding; they are just late.',
        condition: {
            query: {
                kind: 'quantile',
                quantile: 0.95,
                legend: 'read p95',
                metric: 'http.server.request.duration',
                filters: [{ attribute: 'http.request.method', op: '=', value: 'GET' }],
                window: '10m',
            },
            op: '>',
            value: 0.4,
            forDuration: '15m',
        },
        dashboard: 'slo',
        panel: 'read-latency',
        runbook: 'api-latency',
        spec: 'spec/21 §1 (p95 < 400 ms), §7 (ticket for degraded latency)',
    },
    {
        id: 'elevated-errors',
        title: 'More errors than usual',
        tier: 'ticket',
        category: 'errors',
        summary: 'The error rate has stepped up without an outage. Usually a release; the version tag says which.',
        condition: {
            query: { kind: 'rate', legend: 'errors', metric: 'clavian.errors', by: ['service.version'], window: '10m' },
            op: '>',
            value: 1,
            forDuration: '15m',
        },
        dashboard: 'api',
        panel: 'errors-by-release',
        runbook: 'elevated-errors',
        spec: 'spec/21 §7 (ticket for elevated errors)',
    },
    {
        id: 'outbox-backlog',
        title: 'Domain events are piling up',
        tier: 'ticket',
        category: 'availability',
        summary: 'The oldest undispatched outbox row is over five minutes old. Effects that should have ' +
            'happened have not: caches are stale, webhooks are unsent, notifications are waiting.',
        condition: {
            query: { kind: 'value', legend: 'outbox lag', metric: 'clavian.outbox.lag' },
            op: '>',
            value: 300_000,
            forDuration: '5m',
        },
        dashboard: 'queues',
        panel: 'outbox-lag',
        runbook: 'outbox-backlog',
        spec: 'spec/03 §8, spec/21 §6 (queue depths and job lag)',
    },
    {
        id: 'outbox-dead-letter',
        title: 'An event will never be delivered',
        tier: 'ticket',
        category: 'availability',
        summary: 'A delivery has run out of attempts. Something that should have happened never will, and no ' +
            'further retry will change that (issue #33 rule 6).',
        condition: {
            query: { kind: 'value', legend: 'dead lettered', metric: 'clavian.outbox.dead_lettered' },
            op: '>',
            value: 0,
            forDuration: '0m',
        },
        dashboard: 'queues',
        panel: 'outbox-dead-letter',
        runbook: 'outbox-backlog',
        spec: 'spec/03 §8, issue #33 rule 6',
    },
    {
        id: 'job-stopped',
        title: 'A scheduled job has stopped running',
        tier: 'ticket',
        category: 'availability',
        summary: 'No run in 26 hours for a job that runs at least daily. Partitions, retries and archival all ' +
            'fail silently when the scheduler stops.',
        condition: {
            query: { kind: 'total', legend: 'runs', metric: 'clavian.job.runs', by: ['clavian.job'], window: '26h' },
            op: '<',
            value: 1,
            forDuration: '0m',
        },
        dashboard: 'queues',
        panel: 'job-runs',
        runbook: 'outbox-backlog',
        spec: 'spec/21 §6 (queue depths and job lag)',
    },
    {
        id: 'telemetry-cost-anomaly',
        title: 'Telemetry volume has stepped up',
        tier: 'ticket',
        category: 'cost',
        summary: 'Log lines written per second are far above the week-ago baseline. Usually a debug level ' +
            'left on, or a log line added to a hot path (spec/19 §7 rule 5).',
        condition: {
            query: { kind: 'rate', legend: 'log lines', metric: 'clavian.telemetry.log_lines', window: '30m' },
            op: '>',
            // Three times the same half hour a week ago. Traffic has a weekly shape and so does the log
            // volume that follows it; a fixed lines-per-second threshold would fire every Saturday.
            value: 3,
            baseline: { kind: 'relative', offset: '7d' },
            forDuration: '30m',
        },
        dashboard: 'telemetry-cost',
        panel: 'log-lines',
        runbook: 'cost-anomaly',
        spec: 'spec/19 §7 rule 5, spec/21 §7 (ticket for cost anomalies)',
    },
    {
        id: 'heartbeat-logged',
        title: 'Something is logging a heartbeat',
        tier: 'ticket',
        category: 'cost',
        summary: 'Written log lines are climbing at the rate the suppressed ones are, which means a keepalive ' +
            'path has picked up a log line the silent-operation list does not cover. At 50,000 stations ' +
            'this is a five-figure monthly mistake (spec/19 §7 rule 1).',
        condition: {
            query: {
                kind: 'rate',
                legend: 'suppressed lines',
                metric: 'clavian.telemetry.log_lines_suppressed',
                window: '30m',
            },
            op: '<',
            // Nothing suppressed at all while heartbeats are arriving means the rule is not running.
            value: 1,
            forDuration: '30m',
        },
        dashboard: 'telemetry-cost',
        panel: 'log-lines-suppressed',
        runbook: 'cost-anomaly',
        spec: 'spec/19 §7 rule 1',
    },
    {
        id: 'otp-spike',
        title: 'OTP volume has spiked',
        tier: 'ticket',
        category: 'cost',
        summary: 'Either an enumeration attempt or WhatsApp failing over to SMS, and SMS is about sixty times ' +
            'the price. The per-number, per-device and per-IP caps should already be biting (spec/19 §7 rule 3).',
        condition: {
            query: {
                kind: 'rate',
                legend: 'otp sent',
                metric: 'clavian.otp.sent',
                by: ['clavian.message.channel'],
                window: '15m',
            },
            op: '>',
            value: 5,
            forDuration: '15m',
        },
        dashboard: 'messaging-cost',
        panel: 'otp-volume',
        runbook: 'cost-anomaly',
        spec: 'spec/19 §7 rule 3, spec/21 §6 (a spike in OTP)',
    },
    {
        id: 'refund-spike',
        title: 'Refunds have spiked',
        tier: 'ticket',
        category: 'business',
        summary: 'A spike in refunds is either a bad release or a fraud pattern, and both want somebody today.',
        condition: {
            query: { kind: 'rate', legend: 'refunds', metric: 'clavian.refunds', by: ['clavian.venue'], window: '1h' },
            op: '>',
            value: 3,
            forDuration: '1h',
        },
        dashboard: 'payments',
        panel: 'refunds',
        runbook: 'business-anomaly',
        spec: 'spec/21 §6 (a spike in refunds)',
    },
    {
        id: 'revenue-anomaly',
        title: 'Takings are far from normal',
        tier: 'ticket',
        category: 'business',
        summary: 'Revenue at this venue is a long way from what it does at this hour on this day of the week. ' +
            'Low means something is broken; high means something is being counted twice.',
        condition: {
            query: { kind: 'total', legend: 'revenue', metric: 'clavian.revenue', by: ['clavian.venue'], window: '1h' },
            op: '<',
            // Half the same hour a week ago. A venue's Tuesday lunchtime and its Saturday evening are
            // different businesses, so the only honest baseline is the same hour last week.
            value: 0.5,
            baseline: { kind: 'relative', offset: '7d' },
            forDuration: '1h',
        },
        dashboard: 'payments',
        panel: 'success-rate',
        runbook: 'business-anomaly',
        spec: 'spec/21 §6 (revenue anomalies)',
    },
    {
        id: 'session-anomaly',
        title: 'Sessions are far from normal',
        tier: 'ticket',
        category: 'business',
        summary: 'Sessions started at this venue are a long way from the same hour last week. A venue full of ' +
            'people and no sessions is a sign-in path that has broken.',
        condition: {
            query: {
                kind: 'total',
                legend: 'sessions started',
                metric: 'clavian.session.started',
                by: ['clavian.venue'],
                window: '1h',
            },
            op: '<',
            value: 0.5,
            baseline: { kind: 'relative', offset: '7d' },
            forDuration: '1h',
        },
        dashboard: 'fleet',
        panel: 'online',
        runbook: 'business-anomaly',
        spec: 'spec/21 §6 (session anomalies)',
    },
    {
        id: 'cash-variance',
        title: 'Cash variance over threshold',
        tier: 'ticket',
        category: 'business',
        summary: 'A blind count came out too far from expected at shift close. Audited, and somebody should look.',
        condition: {
            query: {
                kind: 'value',
                legend: 'variance',
                metric: 'clavian.cash.variance',
                by: ['clavian.venue', 'clavian.currency'],
            },
            op: '>',
            // ₹500.00 in minor units. The venue-level threshold is a setting when F-OPS-07 lands; this
            // is the platform-wide floor under it.
            value: 50_000,
            forDuration: '0m',
        },
        dashboard: 'payments',
        panel: 'refunds',
        runbook: 'cash-variance',
        spec: 'spec/21 §6 (cash variance over threshold)',
    },
];
export const ALERTS = CATALOGUE;
const BY_ID = new Map(ALERTS.map((alert) => [alert.id, alert]));
export function findAlert(id) {
    return BY_ID.get(id);
}
export function alertsByTier(tier) {
    return ALERTS.filter((alert) => alert.tier === tier);
}
//# sourceMappingURL=alerts.js.map